import React, { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { Mail, Phone, Send, ArrowRight, MessageSquare } from "react-feather";
import Institution from "byzantine/src/Institution";
import { translateSupportContacts } from "byzantine/src/l10n/SettingLocalizers";
import type { SupportContact } from "byzantine/src/types/models/v1/Support.types";
import type { SupportContact as LocalizerSupportContact } from "byzantine/src/types/SettingLocalizers.types";

import {
  useLoadingContext,
  useToggleAgentIQChat,
  catcat as cc,
} from "cerulean";
import { Institution as InstitutionSettings } from "byzantine/src/types/Institution.types";

type LiveChatContact = {
  type: "LIVE_CHAT";
  title: string;
};

type ContactCardContact = SupportContact | LiveChatContact;

type BasicContactUsCardProps = {
  title: string;
  content: React.ReactNode;
  icon: React.ReactNode;
};

function BasicContactUsCard({ title, content, icon }: BasicContactUsCardProps) {
  return (
    <div className="card">
      <div className="contact-us-icon">{icon}</div>
      <div>
        <div className="card-title">{title}</div>
        <div className="contact-us-card-body">{content}</div>
      </div>
    </div>
  );
}

type ContactUsCardProps = {
  contact: ContactCardContact;
  helpEmail?: string;
  helpPhone?: string;
  contactIndex: number;
};

function ContactUsCard({
  contact,
  helpEmail,
  helpPhone,
  contactIndex,
}: ContactUsCardProps) {
  const { onToggle } = useToggleAgentIQChat();
  const { l10n } = useLocalization();

  switch (contact.type) {
    case "LIVE_CHAT":
      return (
        <div
          data-testid={`support-contact-card-${contactIndex}`}
          className="card chat-card"
          role="button"
          onKeyUp={({ key }) => {
            if (key === "Enter") {
              onToggle();
            }
          }}
          aria-label={l10n.getString("support-talk-with-someone")}
          tabIndex={0}
          onClick={onToggle}
        >
          <div className="contact-us-icon">
            <MessageSquare size={20} color="white" />
          </div>
          <div>
            <div className="card-title">{contact.title}</div>
            <div className="contact-us-card-body">
              {l10n.getString("support-talk-with-someone")}
            </div>
          </div>
        </div>
      );
    case "MESSAGE_CENTER":
      return (
        <BasicContactUsCard
          icon={<Send size={20} color="white" />}
          title={contact.title}
          content={
            <a className="message-link" href="/messages">
              {l10n.getString("support-message-center")}{" "}
              <ArrowRight className="arrow-right" size={17} strokeWidth={3} />
            </a>
          }
        />
      );
    case "EMAIL": {
      const email = contact.email || helpEmail;
      if (!email) return null;
      return (
        <BasicContactUsCard
          icon={<Mail size={20} color="white" />}
          title={contact.title}
          content={
            <a
              data-testid={`support-contact-email-link-${contactIndex}`}
              href={`mailto:${email}`}
            >
              {email}
            </a>
          }
        />
      );
    }
    case "PHONE": {
      // If there are no phone numbers with actual phone values and no help phone,
      // don't render the card at all.
      if (!(contact.numbers.find((number) => !!number.phone) || !!helpPhone))
        return null;

      // If there's no helpPhone, let's filter the contact.numbers to only display those with numbers.
      const contactNumbers = helpPhone
        ? contact.numbers
        : contact.numbers.filter((number) => !!number.phone);

      return (
        <BasicContactUsCard
          icon={<Phone size={20} color="white" />}
          title={contact.title}
          content={contactNumbers.map((number, index: number) => (
            <div
              key={index}
              data-testid={`support-contact-phone-row-${contactIndex}-${index}`}
            >
              {number.name}:{" "}
              <a
                data-testid={`support-contact-phone-link-${contactIndex}-${index}`}
                href={`tel:${number.phone || helpPhone}`}
              >
                {number.phone || helpPhone}
              </a>
            </div>
          ))}
        />
      );
    }
    default:
      return null;
  }
}

type ContactUsListProps = {
  contacts: SupportContact[];
};

export default function ContactUsList({ contacts }: ContactUsListProps) {
  const { l10n } = useLocalization();
  const [institutionSettings, setInstitutionSettings] =
    useState<InstitutionSettings>({} as InstitutionSettings);
  const { setIsLoading } = useLoadingContext();
  const updatedContacts: ContactCardContact[] = [...contacts];

  if (institutionSettings.features?.olb_enable_agentiq) {
    updatedContacts.unshift({ type: "LIVE_CHAT", title: "Talk to someone" });
  }

  const translatedContacts = translateSupportContacts(
    updatedContacts as unknown as LocalizerSupportContact[],
    l10n
  ) as ContactCardContact[];

  const fetchSettings = async () => {
    setIsLoading(true);
    const institution = new Institution();
    const settings = await institution.getSettings();
    setInstitutionSettings(settings);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return (
    <div className="contact-us-list">
      <div className="support-section-title">
        {l10n.getString("heading-support-contact")}
      </div>
      <div
        className={cc("cards", {
          "total-cards-4": translatedContacts.length === 4,
        })}
      >
        {translatedContacts.map((contact, index) => (
          <ContactUsCard
            key={`${contact.type}${contact.title}`}
            contact={contact}
            contactIndex={index}
            helpEmail={institutionSettings.help_email}
            helpPhone={institutionSettings.help_phone}
          />
        ))}
      </div>
    </div>
  );
}
